import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PtsProjectService } from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import {
  PtsPillTabsList,
  PtsPillTabsTrigger,
} from "../../components/PtsPillTabs";
import {
  getChangeRequestVisibleTabs,
  type ChangeRequestVisibleTab,
} from "../../utils/rolePermissions";
import AccountantTab from "./AccountantTab";
import BusinessAnalystTab from "./BusinessAnalystTab";
import ManagementTab from "./ManagementTab";

function resolveTab(
  value: string | null,
  allowedTabs: ChangeRequestVisibleTab[]
): ChangeRequestVisibleTab {
  if (
    (value === "management" || value === "accountant" || value === "ba") &&
    allowedTabs.includes(value)
  ) {
    return value;
  }
  return allowedTabs[0] ?? "ba";
}

const ChangeRequestsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = useMemo(() => getChangeRequestVisibleTabs(), []);
  const activeTab = resolveTab(searchParams.get("tab"), allowedTabs);
  const showTabBar = allowedTabs.length > 1;

  useEffect(() => {
    const current = searchParams.get("tab");
    if (!allowedTabs.length) {
      return;
    }
    if (!current || !allowedTabs.includes(current as ChangeRequestVisibleTab)) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", allowedTabs[0]);
          return next;
        },
        { replace: true }
      );
    }
  }, [allowedTabs, searchParams, setSearchParams]);

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["change-request-open-projects"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: employeeOptions = [] } = useQuery({
    queryKey: ["change-request-employees"],
    queryFn: async () => {
      const response = await get<any>("/pts_app/full-employee-list/");
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      return rows
        .map((employee: any) => {
          const value = employee?.emp_id ?? employee?.id ?? employee?.value;
          const label = employee?.emp_name ?? employee?.name ?? employee?.label;
          if (!value) {
            return null;
          }
          return {
            value: String(value),
            label: label ? String(label) : String(value),
          };
        })
        .filter(Boolean) as { value: string; label: string }[];
    },
  });

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    projectOptions.forEach(project => {
      map.set(project.value, project.label);
    });
    return map;
  }, [projectOptions]);

  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    employeeOptions.forEach(emp => {
      map.set(emp.value, emp.label);
    });
    return map;
  }, [employeeOptions]);

  const handleTabChange = (value: string) => {
    const nextTab = resolveTab(value, allowedTabs);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      return next;
    });
  };

  if (!allowedTabs.length) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-0">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600 shadow-sm">
          You do not have permission to access Change Requests.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-6"
      >
        {showTabBar ? (
          <div className="sticky top-[-1.9rem] z-30 flex items-center gap-3 bg-gray-50/95 pb-2 pt-[1.5rem] backdrop-blur-sm">
            <PtsPillTabsList
              className="min-w-0 flex-1 overflow-x-auto md:overflow-x-visible"
              listClassName="w-max min-w-full md:w-full"
            >
              {allowedTabs.includes("ba") ? (
                <PtsPillTabsTrigger
                  value="ba"
                  className="flex-none whitespace-nowrap md:flex-1"
                >
                  Business Analyst
                </PtsPillTabsTrigger>
              ) : null}
              {allowedTabs.includes("management") ? (
                <PtsPillTabsTrigger
                  value="management"
                  className="flex-none whitespace-nowrap md:flex-1"
                >
                  Management
                </PtsPillTabsTrigger>
              ) : null}
              {allowedTabs.includes("accountant") ? (
                <PtsPillTabsTrigger
                  value="accountant"
                  className="flex-none whitespace-nowrap md:flex-1"
                >
                  Accountant
                </PtsPillTabsTrigger>
              ) : null}
            </PtsPillTabsList>
          </div>
        ) : null}

        {allowedTabs.includes("ba") ? (
          <TabsContent value="ba" className="mt-0">
            <BusinessAnalystTab
              projectNameById={projectNameById}
              employeeNameById={employeeNameById}
              employeeOptions={employeeOptions}
            />
          </TabsContent>
        ) : null}
        {allowedTabs.includes("management") ? (
          <TabsContent value="management" className="mt-0">
            <ManagementTab
              projectNameById={projectNameById}
              employeeNameById={employeeNameById}
            />
          </TabsContent>
        ) : null}
        {allowedTabs.includes("accountant") ? (
          <TabsContent value="accountant" className="mt-0">
            <AccountantTab
              projectNameById={projectNameById}
              employeeNameById={employeeNameById}
            />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
};

export default ChangeRequestsPage;
